import { create } from 'zustand';
import type { AppUser, Membership, MeResponse } from '@/types/api';
import { authApi, tryRefresh } from '@/lib/api';
import { clearLocalData } from '@/lib/db';
import { isNative } from '@/lib/platform';
import { restoreTokens } from '@/lib/tokens';
import { queryClient } from '@/lib/queryClient';
import { disableNativePush } from '@/lib/native-push';

type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'anonymous';

interface SessionState {
  status: SessionStatus;
  user: AppUser | null;
  memberships: Membership[];
  activeFamilyId: string | null;
  activeSpaceId: string | null;
  activePersonId: string | null;
  error: string | null;
  bootstrap: () => Promise<void>;
  login: (login: string, password: string) => Promise<void>;
  register: (input: {
    inviteToken: string;
    username: string;
    email?: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  setActiveFamily: (familyId: string) => void;
  setActiveSpace: (spaceId: string | null) => void;
  setActivePerson: (personId: string | null) => void;
}

const ACTIVE_FAMILY_KEY = 'heritage.activeFamilyId';
const ACTIVE_SPACE_KEY = 'heritage.activeSpaceId';
const ACTIVE_PERSON_KEY = 'heritage.activePersonId';

function storedFamilyId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_FAMILY_KEY);
  } catch {
    return null;
  }
}

function storedSpaceId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_SPACE_KEY) || null;
  } catch {
    return null;
  }
}

function storedPersonId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_PERSON_KEY);
  } catch {
    return null;
  }
}

function resolveFamilyId(
  memberships: Membership[],
  current: string | null,
): string | null {
  if (current && memberships.some((m) => m.familyId === current)) return current;
  return memberships[0]?.familyId ?? null;
}

function applyMe(me: MeResponse, currentFamilyId: string | null) {
  const activeFamilyId = resolveFamilyId(me.memberships, currentFamilyId);
  const storedSpace = (() => {
    try {
      return localStorage.getItem(ACTIVE_SPACE_KEY);
    } catch {
      return null;
    }
  })();
  const storedPerson = (() => {
    try {
      return localStorage.getItem(ACTIVE_PERSON_KEY);
    } catch {
      return null;
    }
  })();
  const activeSpaceId =
    storedPerson !== null
      ? null
      : storedSpace !== null
        ? storedSpace || null
        : me.user?.settings?.defaultSpaceId ?? null;
  const activePersonId =
    storedPerson !== null
      ? storedPerson || null
      : activeSpaceId
        ? null
        : me.user?.personId ?? null;
  if (activeFamilyId) {
    try {
      localStorage.setItem(ACTIVE_FAMILY_KEY, activeFamilyId);
    } catch {
      // ignore storage failures
    }
  }
  return {
    status: 'authenticated' as const,
    user: me.user,
    memberships: me.memberships,
    activeFamilyId,
    activeSpaceId,
    activePersonId,
    error: null,
  };
}

let bootstrapPromise: Promise<void> | null = null;

export const useSession = create<SessionState>((set, get) => ({
  status: 'idle',
  user: null,
  memberships: [],
  activeFamilyId: storedFamilyId(),
  activeSpaceId: storedSpaceId(),
  activePersonId: storedPersonId(),
  error: null,

  bootstrap: async () => {
    if (bootstrapPromise) return bootstrapPromise;

    bootstrapPromise = (async () => {
      if (get().status === 'authenticated') return;
      set({ status: 'loading', error: null });

      if (isNative()) {
        const tokens = await restoreTokens();
        if (!tokens) {
          set({ status: 'anonymous', user: null, memberships: [] });
          return;
        }
      }

      try {
        const me = await authApi.me();
        set(applyMe(me, get().activeFamilyId));
      } catch {
        const refreshed = await tryRefresh();
        if (!refreshed) {
          set({
            status: 'anonymous',
            user: null,
            memberships: [],
            error: null,
          });
          return;
        }
        try {
          const me = await authApi.me();
          set(applyMe(me, get().activeFamilyId));
        } catch {
          set({ status: 'anonymous', user: null, memberships: [] });
        }
      }
    })().finally(() => {
      bootstrapPromise = null;
    });

    return bootstrapPromise;
  },

  login: async (login, password) => {
    set({ status: 'loading', error: null });
    try {
      await authApi.login(login, password);
      const me = await authApi.me();
      set(applyMe(me, get().activeFamilyId));
    } catch (error) {
      set({
        status: 'anonymous',
        user: null,
        memberships: [],
        error: error instanceof Error ? error.message : 'Не удалось войти',
      });
      throw error;
    }
  },

  register: async (input) => {
    set({ status: 'loading', error: null });
    try {
      await authApi.register(input);
      const me = await authApi.me();
      set(applyMe(me, get().activeFamilyId));
    } catch (error) {
      set({
        status: 'anonymous',
        user: null,
        memberships: [],
        error:
          error instanceof Error ? error.message : 'Не удалось завершить регистрацию',
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      await disableNativePush().catch(() => undefined);
      await authApi.logout();
    } finally {
      await clearLocalData();
      queryClient.clear();
      set({
        status: 'anonymous',
        user: null,
        memberships: [],
        activeFamilyId: null,
        activeSpaceId: null,
        activePersonId: null,
        error: null,
      });
    }
  },

  refreshMe: async () => {
    try {
      const me = await authApi.me();
      set(applyMe(me, get().activeFamilyId));
    } catch {
      set({ status: 'anonymous', user: null, memberships: [] });
    }
  },

  setActiveFamily: (familyId) => {
    try {
      localStorage.setItem(ACTIVE_FAMILY_KEY, familyId);
      localStorage.removeItem(ACTIVE_SPACE_KEY);
      localStorage.removeItem(ACTIVE_PERSON_KEY);
    } catch {
      // ignore storage failures
    }
    set({ activeFamilyId: familyId, activeSpaceId: null, activePersonId: null });
  },

  setActiveSpace: (spaceId) => {
    try {
      localStorage.setItem(ACTIVE_SPACE_KEY, spaceId ?? '');
    } catch {
      // ignore storage failures
    }
    if (spaceId) {
      try {
        localStorage.setItem(ACTIVE_PERSON_KEY, '');
      } catch {
        // ignore storage failures
      }
    }
    set({ activeSpaceId: spaceId, activePersonId: spaceId ? null : get().activePersonId });
  },

  setActivePerson: (personId) => {
    try {
      if (personId) localStorage.setItem(ACTIVE_PERSON_KEY, personId);
      else localStorage.removeItem(ACTIVE_PERSON_KEY);
    } catch {
      // ignore storage failures
    }
    set({
      activePersonId: personId,
      activeSpaceId: personId ? null : get().activeSpaceId,
    });
  },
}));
